import { useState } from "react";
import axios from "axios";
import logo from "../assets/logo.png";
import {
  CheckCircle2,
  Loader2,
  CalendarPlus,
  User,
  Phone,
  Mail,
  Cake,
  Building2,
  ClipboardList,
  MapPin,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/bookings`;

const VISIT_OPTIONS = [
  "Only Visit",
  "Eye Test",
  "Lens Only",
  "Frame Only",
  "Complete Specs",
  "Specs Repair",
];

const BRANCH_OPTIONS = ["Z.A. Optical Johripur", "Z.A. Optical Jhilmil"];

const emptyForm = {
  customer_name: "",
  phone: "",
  email: "",
  date_of_birth: "",
  branch: "Z.A. Optical Johripur",
  visit_for: "Eye Test",
  address: "",
  notes: "",
};

// Reusable labelled input
function LabeledInput({ label, icon: Icon, required, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon size={13} className="text-slate-500" />}
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function PublicBooking() {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.customer_name.trim() || !form.phone.trim()) {
      setError("Name and phone are required.");
      return;
    }
    if (form.phone.trim().length < 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }
    if (!form.date_of_birth) {
      setError("Date of Birth is required.");
      return;
    }
    if (!form.address.trim()) {
      setError("Address is required.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(API, form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-sky-50 grid place-items-center p-6">
        <div
          data-testid="booking-success"
          className="w-full max-w-md rounded-3xl border border-emerald-200 bg-white p-8 shadow-xl text-center"
        >
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/30">
            <CheckCircle2 className="text-white" size={34} />
          </div>
          <h2 className="mt-5 text-2xl font-bold text-slate-900">Booking Received</h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Thanks <span className="font-semibold text-slate-900">{form.customer_name.split(" ")[0]}</span>!
            Our team will call you on{" "}
            <span className="font-semibold text-slate-900">{form.phone}</span> shortly to confirm.
          </p>
          <div className="mt-6 rounded-xl bg-slate-50 border border-slate-100 p-4 text-left">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Booking Summary</p>
            <ul className="mt-2 text-sm text-slate-700 space-y-1">
              <li><span className="text-slate-500">Visit for:</span> <span className="font-medium">{form.visit_for}</span></li>
              <li><span className="text-slate-500">Branch:</span> <span className="font-medium">{form.branch}</span></li>
            </ul>
          </div>
          <button
            onClick={() => {
              setForm(emptyForm);
              setDone(false);
            }}
            className="mt-6 w-full rounded-xl bg-slate-950 hover:bg-slate-800 px-5 py-3 text-sm font-semibold text-white transition"
          >
            Book Another Visit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-sky-50 py-10 px-4">
      <div className="mx-auto max-w-2xl">
        {/* Brand header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center gap-2.5">
            <div className="h-12 w-12 rounded-2xl overflow-hidden shadow-lg shadow-blue-500/25">
              <img src={logo} alt="ZA Optical" className="h-full w-full object-cover" />
            </div>
            <div className="text-left">
              <p className="text-base font-bold text-slate-900 leading-none">ZA Optical</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-1">
                Optical CRM
              </p>
            </div>
          </div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Book Your Visit
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
            Fill in your details and we&apos;ll reach out to confirm your appointment.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50 overflow-hidden"
          data-testid="public-booking-form"
        >
          {/* Section: Personal details */}
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 sm:px-8 py-4">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-blue-100 grid place-items-center">
                <User size={16} className="text-blue-700" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">Personal Details</p>
                <p className="text-[11px] text-slate-500">How can we reach you?</p>
              </div>
            </div>
          </div>
          <div className="p-6 sm:p-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <LabeledInput label="Full Name" icon={User} required>
                  <input
                    data-testid="pb-name"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    required
                    placeholder="e.g. Rohan Sharma"
                    className="w-full h-11 rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                  />
                </LabeledInput>
              </div>
              <LabeledInput label="Phone" icon={Phone} required>
                <div className="flex rounded-lg border border-slate-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition overflow-hidden">
                  <span className="grid place-items-center px-3 bg-slate-50 text-sm text-slate-500 border-r border-slate-300">
                    +91
                  </span>
                  <input
                    data-testid="pb-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "").slice(0, 10) })}
                    required
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter your number"
                    className="flex-1 h-11 px-3 text-sm outline-none"
                  />
                </div>
              </LabeledInput>
              <LabeledInput label="Email" icon={Mail}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full h-11 rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                />
              </LabeledInput>
              <LabeledInput label="Date of Birth" icon={Cake} required>
                <input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                  required
                  max={new Date().toISOString().split("T")[0]}
                  data-testid="pb-dob"
                  className="w-full h-11 rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                />
              </LabeledInput>
              <LabeledInput label="Address" icon={MapPin} required>
                <input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  required
                  placeholder="City, area"
                  data-testid="pb-address"
                  className="w-full h-11 rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                />
              </LabeledInput>
            </div>
          </div>

          {/* Section: Visit details */}
          <div className="border-t border-b border-slate-100 bg-slate-50/50 px-6 sm:px-8 py-4">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-emerald-100 grid place-items-center">
                <CalendarPlus size={16} className="text-emerald-700" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">Visit Details</p>
                <p className="text-[11px] text-slate-500">Tell us what you need</p>
              </div>
            </div>
          </div>
          <div className="p-6 sm:p-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LabeledInput label="Branch" icon={Building2}>
                <select
                  value={form.branch}
                  onChange={(e) => setForm({ ...form, branch: e.target.value })}
                  data-testid="pb-branch"
                  className="w-full h-11 rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white transition"
                >
                  {BRANCH_OPTIONS.map((b) => (
                    <option key={b}>{b}</option>
                  ))}
                </select>
              </LabeledInput>
              <LabeledInput label="Visit For" icon={ClipboardList}>
                <select
                  value={form.visit_for}
                  onChange={(e) => setForm({ ...form, visit_for: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-300 px-3.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white transition"
                >
                  {VISIT_OPTIONS.map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </LabeledInput>
              <div className="sm:col-span-2">
                <LabeledInput label="Notes / preferred time" icon={Sparkles}>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Any preferred date/time or special requests"
                    className="w-full rounded-lg border border-slate-300 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition resize-none"
                  />
                </LabeledInput>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              data-testid="pb-submit"
              type="submit"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-slate-900 to-slate-950 hover:from-slate-800 hover:to-slate-900 px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/20 disabled:opacity-60 transition"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : null}
              {saving ? "Submitting…" : "Submit Booking"}
            </button>

            <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
              <ShieldCheck size={13} className="text-emerald-500" />
              Your details are stored securely and used only to serve you.
            </div>
          </div>
        </form>

        <p className="mt-6 text-center text-[11px] text-slate-400">
          © 2026 ZA Optical
        </p>
      </div>
    </div>
  );
}
